import React from "react";
import {
  Button,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Image,
  Progress,
  Row,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { useNavigate } from "react-router-dom";
import { Add, Close, FormOutlined } from "@payping/icons";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { DashboardRoutes } from "@/router/constant";
import { updatePersonalInfo } from "@payping/redux/actions";
import { isPersonalRegisterURL } from "@/utils/isRegisterURL";
import { selectAtom } from "jotai/utils";
import { settingsPersonalAtom } from "../state";
import { useAtom } from "jotai";

const mobileFormEditAtom = selectAtom(settingsPersonalAtom, (val) => val.mobileFormEdit);

const MobileSettingsPersonalList: React.FC = () => {
  const { t } = useAppTranslation();
  const isRegisterURL = isPersonalRegisterURL();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const [mobileFormEditState] = useAtom(mobileFormEditAtom);
  const personalDocuments = useAppSelector((state) => state.user.personalInfo?.documents);
  const navigate = useNavigate();
  const { settings } = DashboardRoutes;
  const dispatch = useAppDispatch();

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async () => {
      const finalPersonalData = {
        birthCertificateNumber: mobileFormEditState?.birthCertificateNumber ?? personalInfo?.birthCertificateNumber,
        birthDate: mobileFormEditState?.birthDate ?? personalInfo?.birthDate,
        email: mobileFormEditState?.email ?? personalInfo?.email,
        fatherName: mobileFormEditState?.fatherName ?? personalInfo?.fatherName,
        firstName: mobileFormEditState?.firstName ?? personalInfo?.firstName,
        lastName: mobileFormEditState?.lastName ?? personalInfo?.lastName,
        nationalCode: mobileFormEditState?.nationalCode ?? personalInfo?.nationalCode,
        phoneNumber: mobileFormEditState?.phoneNumber ?? personalInfo?.phoneNumber,
      };

      await dispatch(updatePersonalInfo(finalPersonalData));
      // TODO: the method for uploading user profile picture SHOULD change in backend (upload project)

      if (isRegisterURL) navigate(DashboardRoutes.register.business.url);
      if (!isRegisterURL) navigate(DashboardRoutes.settings.mRoot.url);
    },
    undefined,
    undefined,
    { useProgress: true },
  );

  function stripFirstSegment(url: string) {
    const parts = url.split("/").filter(Boolean);
    if (parts.length > 1) {
      return parts.slice(1).join("/");
    }
    return "no / included";
  }

  function mobilePersonalNavigation(fullUrl: string) {
    const subUrl = stripFirstSegment(fullUrl);
    if (isRegisterURL) return navigate(`/register/${subUrl}`);
    return navigate(`/settings/${subUrl}`);
  }

  return (
    <Drawer
      title={
        isRegisterURL
          ? t("settings.business.paymentGatewayRequestForm")
          : personalInfo?.businessName || t("businessName")
      }
      open
      height={"100%"}
      placement={"bottom"}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(settings.mRoot.url);
          }}
        />
      }
      footer={
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Button onClick={onSubmit} loading={loadingSubmit} className={"tw:w-full"} type={"primary"}>
              {isRegisterURL ? t("saveAndNextLevel") : t("save")}
            </Button>
          </Col>
          <Col span={12}>
            <Button onClick={() => navigate(settings.mRoot.url)} className={"tw:w-full"}>
              {t("close")}
            </Button>
          </Col>
        </Row>
      }
    >
      <Row>
        <Col span={24}>
          <Descriptions
            className="tw:w-full"
            column={1}
            layout={"vertical"}
            size={"middle"}
            title={
              <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
                <Typography.Title className={"tw:m-0! tw:font-medium"} level={4}>
                  {t("profile.main.personal")}
                </Typography.Title>
                {isRegisterURL ? (
                  <Progress steps={3} percent={30} format={(percent) => `${(percent || 30) / 30} از 3`} />
                ) : null}
              </Space>
            }
          >
            <Descriptions.Item label={t("firstName")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.name.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.firstName ?? personalInfo?.firstName) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t("lastName")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.lastName.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.lastName ?? personalInfo?.lastName) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t("profile.fatherName")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.fatherName.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.fatherName ?? personalInfo?.fatherName) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t("phoneNumber")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.phoneNumber.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.phoneNumber ?? personalInfo?.phoneNumber) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t("birthDate")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.birthDate.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                {toJalali({
                  date: mobileFormEditState?.birthDate ?? personalInfo?.birthDate,
                  toFormat: dateFormats.ui.jalaliDate,
                }).string || t("notRegistered")}
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t("profile.birthCertificateNumber")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.birthCertificateNumber.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.birthCertificateNumber ?? personalInfo?.birthCertificateNumber) ||
                    t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t("nationalCode")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.nationalCode.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.nationalCode ?? personalInfo?.nationalCode) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t("email")}>
              <Space
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.email.url)}
                size={"small"}
                orientation={"vertical"}
                className={"tw:w-full"}
              >
                <Typography.Text>
                  {(mobileFormEditState?.email ?? personalInfo?.email) || t("notRegistered")}
                </Typography.Text>
                <Divider className={"background--gray100 tw:m-0!"} />
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col span={24} className={"tw:mb-12! tw:mt-8!"}>
          <Space orientation={"vertical"} size={"large"} className={"tw:w-full"}>
            <Space className={"tw:flex tw:w-full tw:justify-between"}>
              <Typography.Text type={"secondary"}>{t("settings.nationalIdAndCodePhoto")}</Typography.Text>
              <FormOutlined width={14} onClick={() => mobilePersonalNavigation(settings.mEditPersonal.documents.url)} />
            </Space>

            {personalDocuments ? (
              <Space size={"middle"} className={"tw:w-full"}>
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    {personalDocuments?.map((imgs, i) => (
                      <Image key={i} className={"p-1"} width={100} src={imgs.serverName} />
                    ))}
                  </Col>
                </Row>
              </Space>
            ) : (
              <Button
                icon={<Add width={14} height={14} />}
                onClick={() => mobilePersonalNavigation(settings.mEditPersonal.documents.url)}
              >
                {t("add.add")}
              </Button>
            )}
          </Space>
        </Col>
      </Row>
    </Drawer>
  );
};

export default MobileSettingsPersonalList;
